import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const Cart = () => {
  const { items, updateQty, removeItem, total, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkout, setCheckout] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    email: user?.email || "",
    address: "", city: "", state: "MO", zip_code: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const startCheckout = () => {
    if (!user) {
      toast.error("Please sign in to checkout");
      return navigate("/login", { state: { from: { pathname: "/cart" } } });
    }
    setCheckout(true);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      await api.post("/orders", { ...form, items });
      toast.success("Order placed! (mock payment)");
      clearCart();
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-28 text-center" data-testid="cart-empty">
        <h1 className="font-display text-6xl mb-4">Your Cart Is Empty</h1>
        <p className="text-[#4A4A4A] mb-8">Time to grab some KC gear.</p>
        <button onClick={() => navigate("/shop")} className="btn-primary px-8 py-4" data-testid="go-shop">Shop Now</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12" data-testid="cart-page">
      <h1 className="font-display text-6xl md:text-7xl mb-10">{checkout ? "Checkout" : "Your Cart"}</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.size}`} className="flex gap-4 border-2 border-[#1C1C1C] bg-[#F5F2EA] p-3" data-testid={`cart-item-${i.product_id}`}>
              <img src={i.image} alt={i.name} className="w-24 h-24 object-cover border-2 border-[#1C1C1C]" />
              <div className="flex-1">
                <h3 className="font-display text-2xl leading-none">{i.name}</h3>
                <p className="text-sm text-[#4A4A4A] mt-1">Size: {i.size}</p>
                <p className="font-bold text-[#C0392B] mt-1">${i.price.toFixed(2)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeItem(i.product_id, i.size)} className="text-[#C0392B] hover:scale-110 transition" data-testid={`remove-${i.product_id}`}>
                  <Trash2 className="w-5 h-5" />
                </button>
                <div className="flex items-center border-2 border-[#1C1C1C]">
                  <button onClick={() => updateQty(i.product_id, i.size, i.quantity - 1)} className="px-2 py-1 hover:bg-[#EDE8DD]"><Minus className="w-3 h-3" /></button>
                  <span className="px-3 font-bold text-sm">{i.quantity}</span>
                  <button onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)} className="px-2 py-1 hover:bg-[#EDE8DD]"><Plus className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-2 border-[#1C1C1C] hard-shadow bg-[#F5F2EA] p-6 h-fit">
          <h2 className="font-display text-3xl mb-4">Order Summary</h2>
          <div className="flex justify-between mb-2"><span>Subtotal</span><span className="font-bold">${total.toFixed(2)}</span></div>
          <div className="flex justify-between mb-2 text-sm text-[#4A4A4A]"><span>Shipping</span><span>Calculated at fulfillment</span></div>
          <div className="border-t-2 border-[#1C1C1C] my-4" />
          <div className="flex justify-between text-2xl font-bold"><span>Total</span><span className="text-[#C0392B]" data-testid="cart-total">${total.toFixed(2)}</span></div>

          {!checkout ? (
            <button onClick={startCheckout} className="btn-primary w-full px-8 py-4 mt-6" data-testid="proceed-checkout">Proceed to Checkout</button>
          ) : (
            <form onSubmit={placeOrder} className="mt-6 space-y-3" data-testid="checkout-form">
              <input required name="customer_name" value={form.customer_name} onChange={onChange} placeholder="Full Name" className="w-full px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="checkout-name" />
              <input required name="email" type="email" value={form.email} onChange={onChange} placeholder="Email" className="w-full px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="checkout-email" />
              <input required name="address" value={form.address} onChange={onChange} placeholder="Street Address" className="w-full px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="checkout-address" />
              <div className="grid grid-cols-3 gap-2">
                <input required name="city" value={form.city} onChange={onChange} placeholder="City" className="px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="checkout-city" />
                <input required name="state" value={form.state} onChange={onChange} placeholder="State" className="px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="checkout-state" />
                <input required name="zip_code" value={form.zip_code} onChange={onChange} placeholder="ZIP" className="px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="checkout-zip" />
              </div>
              <div className="bg-[#EDE8DD] border-2 border-dashed border-[#4A4A4A] p-3 text-xs text-[#4A4A4A]">
                💳 Mock payment — no card required. Stripe can be wired in later.
              </div>
              <button type="submit" disabled={placing} className="btn-primary w-full px-8 py-4 disabled:opacity-60" data-testid="place-order">
                {placing ? "Placing Order…" : `Pay $${total.toFixed(2)} (Mock)`}
              </button>
              <button type="button" onClick={() => setCheckout(false)} className="w-full text-sm font-semibold underline underline-offset-4 mt-1 inline-flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to cart
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
